<template>
  <div>
    <div class="form__collection">
      <div class="form__group -has-label" :class="{ error: errors.has('category_id') }">
        <label for="category_id">Topic</label>
        <div class="select-style">
          <select
            id="category_id"
            v-model="selectedCat"
            v-validate="'min_value: 1'"
            name="category_id"
            data-vv-as="Topic"
            data-vv-validate-on="blur|change"
            @change="resetSubcats"
          >
            <option :value="0">
              Select
            </option>
            <option v-for="cat in categories" :key="cat.id" :value="cat.id">
              {{ cat.title }}
            </option>
          </select>
        </div>
        <span v-if="errors.has('category_id')" class="message">{{
          errors.first('category_id')
        }}</span>
      </div>

      <div class="form__group -has-label" :class="{ error: errors.has('subcategory_id') }">
        <label for="subcategory_id">Subtopic</label>
        <div class="select-style">
          <select
            id="subcategory_id"
            v-model="selectedSubcat"
            v-validate="'min_value: 1'"
            :disabled="selectedCat < 1"
            name="subcategory_id"
            data-vv-as="Subtopic"
            data-vv-validate-on="blur|change"
          >
            <option :value="0">
              Select
            </option>
            <option v-for="subcat in subcategories" :key="subcat.id" :value="subcat.id">
              {{ subcat.title }}
            </option>
          </select>
        </div>
        <span v-if="errors.has('subcategory_id')" class="message">{{
          errors.first('subcategory_id')
        }}</span>
      </div>
    </div>

    <div class="form__group -has-label" :class="{ error: errors.has('title') }">
      <label for="title">Title</label>
      <input
        id="title"
        v-model="title"
        v-validate="'required|max: 115'"
        name="title"
        type="text"
        maxlength="115"
        data-vv-as="Title"
      />
      <span v-if="errors.has('title')" class="iconfont-error" aria-hidden="true" />
      <span v-if="errors.has('title')" class="message"> {{ errors.first('title') }}</span>
    </div>

    <div class="form__group -textarea -has-label" :class="{ error: errors.has('body') }">
      <label for="body">Message</label>
      <textarea
        id="body"
        v-model="body"
        v-validate="'required|min: 20|max: 100000'"
        cols="50"
        name="body"
        placeholder="What's on your mind?  Start typing here..."
        rows="6"
        maxlength="100000"
        data-vv-as="Message"
        data-vv-validate-on="blur|change"
      />
      <span v-if="errors.has('body')" class="iconfont-error" aria-hidden="true" />
      <span v-if="errors.has('body')" class="message">{{ errors.first('body') }}</span>
    </div>

    <div class="form__group -image -has-label" :class="{ error: errors.has('image') }">
      <label for="image">Image</label>
      <div class="discussion-image-upload" :class="{ error: errors.has('image') }">
        <div class="file-input">
          <span v-if="!hasValidImage" class="iconfont-image" />
          <label v-if="!hasValidImage" class="image" for="image"
            ><strong>Attach an Image</strong> (10mb limit)</label
          >
          <!--<label class="file-name" for="image" v-if="image && !errors.has('image')"><strong>{{ image }}</strong></label>-->
          <input
            id="image"
            ref="fileInput"
            v-validate="'image|size:10000'"
            class="form-control"
            name="image"
            type="file"
            @change="onFileChange"
          />
          <input v-model="removeImage" name="image_remove" type="hidden" />
        </div>
        <div v-if="hasValidImage" class="file-remove">
          <button class="btn -image-remove" title="Remove Image" @click.prevent="removeFile">
            <span class="iconfont-menu-close" />Remove Image
          </button>
          <img :src="discussionImage" alt="" />
        </div>
      </div>
      <span v-if="errors.has('image')" class="iconfont-error" aria-hidden="true" />
      <span v-if="errors.has('image')" class="message">{{ errors.first('image') }}</span>
    </div>

    <div class="posting-as">
      <h5 class="heading">
        Posting As
      </h5>

      <div class="posting-identity">
        <div class="posting-avatar">
          <img v-if="user.avatar" :alt="user.username" :src="user.avatar" />
          <div v-else class="avatar-placeholder demi">
            <span>{{ user.displayName.charAt(0) }}</span>
          </div>
        </div>
        <div class="posting-name">
          {{ user.displayName }}
        </div>
        <a href="/my/account" class="change-button">Change</a>
      </div>
    </div>

    <div v-if="isEdit" class="discussion-edit-actions">
      <div class="discussion-edit-buttons">
        <submit-button
          :submitting="submitting"
          label="Save Changes"
          submitting-label="Saving…"
          classes="-main no-margin editing"
          @submit-action="validateAllAndSubmit"
        />
        <a class="btn -link no-margin" :href="url">Cancel</a>
      </div>
      <a class="link small demi flex-right" href="/page/discussion-policy" target="_blank"
        >Discussion Policy</a
      >
    </div>

    <div v-else class="discussion-action">
      <div class="flex-right">
        <a class="link small demi" href="/page/discussion-policy" target="_blank"
          >Discussion Policy</a
        >
      </div>
      <submit-button
        :submitting="submitting"
        label="Publish"
        submitting-label="Publishing…"
        classes="-main -large-medium-only"
        @submit-action="validateAllAndSubmit"
      />
    </div>
  </div>
</template>

<script>
// @flow
import { mapState, mapActions } from 'vuex'
import submitButton from 'app/components/buttons/submit-button.vue'

export default {
  components: { submitButton },
  data() {
    return {
      // the actual image file as a string, after the file is selected
      image: '',
      /* eslint-disable camelcase */
      selectedCat: Laravel.discussion.category_id,
      selectedSubcat: Laravel.discussion.subcategory_id,
      id: Laravel.discussion.id,
      title: Laravel.discussion.title,
      body: Laravel.discussion.body,
      seoUrl: Laravel.discussion.seo_url,
      // on edit the url to existing file
      imageUrl: Laravel.discussion.image_url,
      /* eslint-enable camelcase */
      submitting: false,
      removeImage: 0,
      form: document.getElementById('discussion_form'),
      // RECAPTCHA
      //      recaptchaVerified: false,
      //      pleaseTickRecaptchaMessage: '',
      //      grecaptchaPosition: 0,
      //      isIOS: false
    }
  },
  computed: {
    categories() {
      return Laravel.communityCategories
    },
    subcategories() {
      const category = Laravel.communityCategories.filter(cat => {
        return cat.id === this.selectedCat
      })

      return category[0] ? category[0].subcategories : []
    },
    isEdit() {
      return Boolean(this.id)
    },
    discussionImage() {
      return this.image || this.imageUrl
    },
    hasValidImage() {
      return this.discussionImage && !this.errors.has('image')
    },
    url() {
      return '/community/discussion/' + this.id + '/' + this.seoUrl
    },
    ...mapState({
      user: (state: State) => state.user,
    }),
  },
  // RECAPTCHA
  // mounted() {
  //     // add fix for iOS scrolling issue
  //     // https://github.com/google/recaptcha/issues/130#issuecomment-362308382
  //     this.isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent)
  //
  //     if (this.isIOS) {
  //       window.addEventListener('scroll', this.scrollMethod, false)
  //     }
  // },
  created() {
    this.$validator.updateDictionary({
      en: {
        messages: {
          required: $field => $field + ' is required',
          min: ($field, $value) => 'Must be at least ' + $value + ' characters',
          max: ($field, $value) => 'Must be less than ' + $value + ' characters',
          // eslint-disable-next-line
          min_value: $field => $field + ' is required',
          image: 'Must be an image under 10MB',
          size: 'Must be an image under 10MB',
        },
      },
    })
  },
  methods: {
    // RECAPTCHA
    // isScrolledIntoView(elem: Object) {
    //   // todo: not sure what real value of header_height should be
    //   const HEADER_HEIGHT = 10 // Height of header/menu fixed if exists
    //   const elemRect = elem.getBoundingClientRect()
    //   return (elemRect.top - HEADER_HEIGHT >= 0 && elemRect.bottom <= window.innerHeight)
    // },
    // scrollMethod() {
    //   const recaptchaElements = document.querySelectorAll('.g-recaptcha')
    //
    //   // could not figure out how to get 'this' working with this.grecaptchaPostion
    //   // Array.prototype.forEach.call(recaptchaElements, function (element) {
    //   //   if (isScrolledIntoView(element)) {
    //   //     // eslint-disable-next-line
    //   //     this.grecaptchaPosition = window.scrollY || document.body.scrollTop
    //   //   }
    //   // })
    //
    //   if (typeof recaptchaElements[0] === 'undefined') {
    //     return
    //   }
    //
    //   const recaptchaElement = recaptchaElements[0]
    //
    //   if (this.isScrolledIntoView(recaptchaElement)) {
    //     // eslint-disable-next-line
    //     this.grecaptchaPosition = document.documentElement.scrollTop || document.body.scrollTop
    //   }
    // },
    // note: not sure what response is if String or not, but only eslint cares
    // markRecaptchaAsVerified(response: String) {
    //   this.recaptchaVerified = true
    //   this.pleaseTickRecaptchaMessage = ''
    //
    //   if (this.isIOS && this.grecaptchaPosition !== undefined) {
    //     window.scrollTo(0, this.grecaptchaPosition)
    //   }
    // },

    onFileChange(e: any) {
      // @TODO fix this event type
      const target = e.target || e.dataTransfer
      const files = target && target.files
      if (!files.length) {
        return
      }

      this.createImage(files[0])
    },

    createImage(file: any) {
      // @TODO fix this type
      const reader = new window.FileReader()

      reader.onload = e => {
        this.image = e.target.result
      }

      reader.readAsDataURL(file)
    },

    removeFile() {
      this.$refs.fileInput.value = ''
      this.image = ''

      // set a flag to tell server to remove the file.  Only do this if there is an existing file
      if (this.imageUrl) {
        this.removeImage = 1
      }
      this.imageUrl = ''
    },

    resetSubcats() {
      this.selectedSubcat = 0
    },

    validateAllAndSubmit() {
      // RECAPTCHA
      // for now it does its own verification
      // if (!this.recaptchaVerified) {
      //   this.pleaseTickRecaptchaMessage = 'Please check box'
      //   return // prevent form from submitting
      // }

      if (this.submitting) {
        return
      }

      this.submitting = true
      this.$validator.validateAll().then(result => {
        if (!result) {
          this.submitting = false
          return
        }

        const formData = new window.FormData(this.form)
        const multipartHeader = { headers: { 'Content-Type': 'multipart/form-data' } }
        const url = this.isEdit ? '/community/edit/' + this.id : '/community/create'
        this.$http.post(url, formData, multipartHeader).then(
          res => {
            // response is url to redirect to
            window.location = res.data.redirectTo
          },
          err => {
            // validation errors
            if (err.status === 422) {
              const errorFields = Object.keys(err.data.errors)

              // turn Laravel Errors into vee validate errors
              errorFields.map(field => {
                const errorString = err.data.errors[field].join(', ')
                this.errors.add(field, errorString)
              })
              // 401 is not logged in, 419 is csrf which is likely logged out
            } else if (err.status === 401 || err.status === 419) {
              this.showWasLoggedInModal()
              // 409 discussion doesn't exist or user doesn't own, 500 or other error
            } else {
              this.showErrorModal()
            }

            this.submitting = false
          },
        )
      })
    },
    ...mapActions(['showErrorModal', 'showWasLoggedInModal']),
  },
}
</script>
